import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from './common/SafeIcon';
import './App.css';
import ResourceCard from './components/ResourceCard';
import FeedbackForm from './components/FeedbackForm';
import StoryForm from './components/StoryForm';
import WelcomeMessage from './components/WelcomeMessage';
import { fetchResourcesFromSheet, parseRating, isVerified } from './services/googleSheetService';

const { FiSearch, FiFilter } = FiIcons;

function App() {
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredResults, setFilteredResults] = useState([]);
  const [resources, setResources] = useState([]);
  const [activeCategory, setActiveCategory] = useState('All');
  const [showStoryForm, setShowStoryForm] = useState(false);
  const [showFeedbackForm, setShowFeedbackForm] = useState(false);
  const [hasViewedWelcome, setHasViewedWelcome] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch resources from Google Sheet
  useEffect(() => {
    const getResources = async () => {
      try {
        setIsLoading(true);
        const data = await fetchResourcesFromSheet();
        console.log(`Successfully loaded ${data.length} resources from Google Sheets`);
        setResources(data);
        setError(null); // Clear any previous errors
      } catch (err) {
        console.error("Error fetching resources:", err);
        setError("Failed to load resources. Please try again later.");
        // Fallback to sample data if fetch fails
        setResources(sampleResources);
      } finally {
        setIsLoading(false);
      }
    };

    getResources();
  }, []);

  // Category mapping for filtering - Updated to work with various field names from spreadsheet
  const categoryMap = {
    "All": () => true,
    "Housing": (resource) => 
      (resource.section && resource.section.toLowerCase().includes('housing')) ||
      (resource.section && resource.section.toLowerCase().includes('homeless')) ||
      (resource.section && resource.section.toLowerCase().includes('shelter')) ||
      (resource.section && resource.section.toLowerCase().includes('rent')) ||
      (resource.category && resource.category.toLowerCase().includes('housing')) ||
      (resource.services && resource.services.toLowerCase().includes('housing')),
    "Employment": (resource) => 
      (resource.section && resource.section.toLowerCase().includes('employment')) ||
      (resource.section && resource.section.toLowerCase().includes('job')) ||
      (resource.category && resource.category.toLowerCase().includes('employment')) ||
      (resource.services && resource.services.toLowerCase().includes('job')),
    "Health": (resource) => 
      (resource.section && resource.section.toLowerCase().includes('health')) ||
      (resource.section && resource.section.toLowerCase().includes('medical')) ||
      (resource.section && resource.section.toLowerCase().includes('dental')) ||
      (resource.section && resource.section.toLowerCase().includes('eye')) ||
      (resource.category && resource.category.toLowerCase().includes('health')) ||
      (resource.services && resource.services.toLowerCase().includes('medical')),
    "Legal": (resource) => 
      (resource.section && resource.section.toLowerCase().includes('legal')) ||
      (resource.category && resource.category.toLowerCase().includes('legal')) ||
      (resource.services && resource.services.toLowerCase().includes('legal')),
    "Pregnancy": (resource) => 
      (resource.section && resource.section.toLowerCase().includes('pregnancy')) ||
      (resource.section && resource.section.toLowerCase().includes('prenatal')) ||
      (resource.category && resource.category.toLowerCase().includes('pregnancy')) ||
      (resource.services && resource.services.toLowerCase().includes('pregnancy')),
    "Substance Abuse": (resource) => 
      (resource.section && resource.section.toLowerCase().includes('substance')) ||
      (resource.section && resource.section.toLowerCase().includes('rehabilitation')) ||
      (resource.category && resource.category.toLowerCase().includes('substance')) ||
      (resource.services && resource.services.toLowerCase().includes('substance')),
    "Domestic Violence": (resource) => 
      (resource.section && resource.section.toLowerCase().includes('domestic')) ||
      (resource.section && resource.section.toLowerCase().includes('violence')) ||
      (resource.section && resource.section.toLowerCase().includes('assault')) ||
      (resource.category && resource.category.toLowerCase().includes('domestic')) ||
      (resource.services && resource.services.toLowerCase().includes('violence')),
    "Youth": (resource) => 
      (resource.section && resource.section.toLowerCase().includes('youth')) ||
      (resource.section && resource.section.toLowerCase().includes('teen')) ||
      (resource.section && resource.section.toLowerCase().includes('children')) ||
      (resource.category && resource.category.toLowerCase().includes('youth')) ||
      (resource.services && resource.services.toLowerCase().includes('youth')),
    "Veterans": (resource) => 
      (resource.section && resource.section.toLowerCase().includes('veteran')) ||
      (resource.category && resource.category.toLowerCase().includes('veteran')) ||
      (resource.services && resource.services.toLowerCase().includes('veteran')),
    "Elder Care": (resource) => 
      (resource.section && resource.section.toLowerCase().includes('elder')) ||
      (resource.section && resource.section.toLowerCase().includes('aging')) ||
      (resource.category && resource.category.toLowerCase().includes('elder')) ||
      (resource.services && resource.services.toLowerCase().includes('aging')),
  };

  // Set welcome as viewed when category changes or search query changes
  useEffect(() => {
    if (activeCategory !== 'All' || searchQuery.trim() !== '') {
      setHasViewedWelcome(true);
    }
  }, [activeCategory, searchQuery]);

  // Filter resources based on search query and active category
  useEffect(() => {
    const filtered = resources.filter(resource => {
      // First apply category filter
      if (!categoryMap[activeCategory](resource)) return false;

      // Check if resource is verified (80%+ upvotes with 10+ total votes)
      const rating = parseRating(resource.rating || '');
      if (rating.total >= 10 && !isVerified(rating)) {
        return false; // Filter out unverified resources with 10+ votes
      }

      // If no search query, return all resources that match the category
      if (!searchQuery.trim()) return true;

      // Otherwise, apply search filter across multiple fields
      const query = searchQuery.toLowerCase();
      return (
        (resource.city && resource.city.toLowerCase().includes(query)) ||
        (resource.zipcode && resource.zipcode.includes(query)) ||
        (resource.zip_code && resource.zip_code.includes(query)) ||
        (resource.section && resource.section.toLowerCase().includes(query)) ||
        (resource.category && resource.category.toLowerCase().includes(query)) ||
        (resource.organizationname && resource.organizationname.toLowerCase().includes(query)) ||
        (resource.organization_name && resource.organization_name.toLowerCase().includes(query)) ||
        (resource.services && resource.services.toLowerCase().includes(query)) ||
        (resource.address && resource.address.toLowerCase().includes(query))
      );
    });

    setFilteredResults(filtered);
  }, [searchQuery, activeCategory, resources]);

  // Handle vote updates from ResourceCard
  const handleVoteUpdate = (resourceId, newRating) => {
    setResources(prevResources => 
      prevResources.map(resource => 
        resource.id === resourceId 
          ? { ...resource, rating: `up:${newRating.up},down:${newRating.down},total:${newRating.total}` }
          : resource
      )
    );
  };

  // Sample data as fallback if Google Sheets API fails
  const sampleResources = [
    {
      id: 1,
      section: "Pregnancy Resources",
      organizationname: "Agape Pregnancy Center",
      locationname: "Merritt Island Suite C",
      address: "300 Magnolia Avenue Suite C",
      city: "Merritt Island",
      zipcode: "32952",
      phonenumber: "321-454-9853",
      alternatephone: "800-712-4357",
      website: "www.apcbrevard.com",
      email: "",
      services: "Offers physical, emotional, and spiritual support to individuals facing crisis pregnancies and abortion issues.",
      hours: "Mon 10–5, Tue 12–4, Wed 2–6, Thu 10–5",
      notes: "No appointments necessary, but encouraged. Two locations.",
      rating: "up:5,down:1,total:6"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-4 py-6">
          <div className="text-center">
            <motion.div 
              initial={{ opacity: 0, y: -20 }} 
              animate={{ opacity: 1, y: 0 }} 
              transition={{ duration: 0.6 }}
              className="flex flex-col items-center justify-center gap-3 mb-3"
            >
              {/* Logo with hyperlink */}
              <a href="https://www.thehousepalmbay.com/" target="_blank" rel="noopener noreferrer">
                <img 
                  src="https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1753659090192-blob" 
                  alt="Restoration Ministries Logo" 
                  className="h-24 mb-2 hover:opacity-90 transition-opacity"
                />
              </a>
              <h1 className="text-3xl font-bold text-gray-800">Restoration Ministries</h1>
            </motion.div>
            <motion.p 
              initial={{ opacity: 0, y: -10 }} 
              animate={{ opacity: 1, y: 0 }} 
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-gray-600 max-w-2xl mx-auto leading-relaxed"
            >
              Creating a community of hope where people can belong, connect, and find a second chance through faith and support. Find the resources you need to rebuild and thrive.
            </motion.p>
            {/* Show total resource count when loaded */}
            {!isLoading && resources.length > 0 && (
              <motion.p 
                initial={{ opacity: 0 }} 
                animate={{ opacity: 1 }} 
                transition={{ duration: 0.6, delay: 0.4 }}
                className="text-sm text-indigo-600 mt-2"
              >
                {resources.length} resources available to help you
              </motion.p>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8">
        {/* Search Section */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }} 
          animate={{ opacity: 1, y: 0 }} 
          transition={{ duration: 0.6, delay: 0.3 }}
          className="bg-white rounded-2xl shadow-lg p-8 mb-8"
        >
          <div className="text-center mb-6">
            <h2 className="text-2xl font-semibold text-gray-800 mb-2">Find Resources Near You</h2>
            <p className="text-gray-600">Search by city, ZIP code, or type of service</p>
          </div>
          <div className="max-w-md mx-auto">
            <div className="relative">
              <SafeIcon icon={FiSearch} className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Enter city, ZIP code, or service type..."
                className="w-full pl-12 pr-4 py-4 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-200 text-lg"
              />
            </div>
          </div>
        </motion.div>

        {/* Category Filters - Improved scrollbar hiding */}
        <div className="mb-8">
          <div className="overflow-x-auto scrollbar-hide">
            <div className="flex space-x-2 min-w-max pb-1">
              {Object.keys(categoryMap).map((category) => (
                <button
                  key={category}
                  onClick={() => setActiveCategory(category)}
                  className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                    activeCategory === category
                      ? 'bg-indigo-600 text-white'
                      : 'bg-white text-gray-700 hover:bg-indigo-100'
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Welcome Message - Only show when first visiting with All category and no search */}
        {!hasViewedWelcome && activeCategory === "All" && !searchQuery && (
          <WelcomeMessage />
        )}

        {/* Results Section */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6 }}
        >
          {isLoading ? (
            <div className="flex justify-center items-center py-20">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
            </div>
          ) : error ? (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
              <p>{error}</p>
            </div>
          ) : (
            <>
              <h3 className="text-xl font-semibold text-gray-800 mb-6">
                {filteredResults.length > 0 ? (
                  <>
                    Showing {filteredResults.length} resource{filteredResults.length !== 1 ? 's' : ''}
                    {searchQuery ? ` for "${searchQuery}"` : ''}
                    {activeCategory !== "All" ? ` in ${activeCategory}` : ''}
                  </>
                ) : (
                  <div className="bg-white rounded-xl shadow-lg p-8 max-w-md mx-auto text-center">
                    <h3 className="text-lg font-semibold text-gray-800 mb-2">No Resources Found</h3>
                    <p className="text-gray-600 mb-4">
                      We couldn't find any resources matching your search criteria. Try broadening your search or select a different category.
                    </p>
                    <p className="text-sm text-gray-500">
                      Have a prayer request or need help finding your way? <a href="mailto:restorationministries@pxdmail.com" className="text-indigo-600 hover:text-indigo-800">Email us</a>
                    </p>
                  </div>
                )}
              </h3>
              {filteredResults.length > 0 && (
                <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                  {filteredResults.map((resource, index) => (
                    <ResourceCard 
                      key={resource.id || index} 
                      resource={resource} 
                      index={index}
                      onVoteUpdate={handleVoteUpdate}
                    />
                  ))}
                </div>
              )}
            </>
          )}
        </motion.div>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 mt-16">
        <div className="max-w-6xl mx-auto px-4 py-8">
          <div className="flex flex-col md:flex-row justify-between items-center mb-6">
            <p className="text-gray-600 mb-4 md:mb-0">
              Have a prayer request or need help finding your way? <a href="mailto:restorationministries@pxdmail.com" className="text-indigo-600 hover:text-indigo-800">Email us</a>
            </p>
            <div className="flex space-x-4">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => setShowFeedbackForm(true)}
                className="px-4 py-2 bg-indigo-100 hover:bg-indigo-200 text-indigo-800 rounded-lg transition-colors"
              >
                Share Feedback
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => setShowStoryForm(true)}
                className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors"
              >
                Share Your Story
              </motion.button>
            </div>
          </div>
          <p className="text-sm text-gray-500 text-center">
            © 2025 Restoration Ministries. Walking with you on your journey of hope and healing.
          </p>
        </div>
      </footer>

      {/* Story Form Modal */}
      {showStoryForm && (
        <StoryForm onClose={() => setShowStoryForm(false)} />
      )}

      {/* Feedback Form Modal */}
      {showFeedbackForm && (
        <FeedbackForm onClose={() => setShowFeedbackForm(false)} />
      )}
    </div>
  );
}

export default App;